import React, { useState } from 'react';
import { Scanner } from '@yudiel/react-qr-scanner';
import { 
  MdQrCodeScanner, 
  MdError, 
  MdCheckCircle, 
  MdRefresh, 
  MdCameraAlt,
  MdInfo 
} from 'react-icons/md';
import { getRegistrationDetailByQRCode } from '../api/registration';

const QRScanner = ({ onScanSuccess, onScanError, theme }) => {
  const [scanning, setScanning] = useState(true);
  const [error, setError] = useState(null);
  const [processing, setProcessing] = useState(false);

  const handleScan = async (result) => {
    if (!result || !result[0]?.rawValue) return;
    
    try {
      setScanning(false);
      setProcessing(true);
      const qrCode = result[0].rawValue;
      
      // Verify the QR code with the API
      const response = await getRegistrationDetailByQRCode(qrCode);
      
      if (response.success && response.data) {
        onScanSuccess?.(response.data);
        setProcessing(false);
      } else {
        throw new Error('Invalid QR code or registration not found');
      }
    } catch (err) {
      console.error('QR Scan error:', err);
      setError(err.message || 'Failed to verify QR code');
      setProcessing(false);
      onScanError?.(err.message || 'Failed to verify QR code');
      // Resume scanning after error
      setTimeout(() => {
        setScanning(true);
        setError(null);
      }, 3000);
    }
  };

  const handleError = (err) => {
    console.error('Scanner error:', err);
    setError('Camera access denied or not available');
    onScanError?.('Camera access denied or not available');
  };

  const resumeScanning = () => {
    setScanning(true);
    setError(null);
    setProcessing(false);
  };

  return (
    <div className="w-full max-w-md mx-auto">
      {/* Scanner Header */}
      <div className="bg-white border border-gray-200 rounded-lg p-4 mb-4">
        <div className="flex items-center space-x-3 mb-3">
          <div className={`w-10 h-10 rounded-lg bg-gradient-to-r ${theme?.gradient || 'from-blue-600 to-indigo-600'} flex items-center justify-center`}>
            <MdQrCodeScanner className="text-white text-xl" />
          </div>
          <div>
            <h3 className="text-lg font-semibold text-gray-900">QR Code Scanner</h3>
            <p className="text-sm text-gray-600">Scan registration QR codes</p>
          </div>
        </div>
        
        <div className="flex items-center space-x-2 text-sm">
          <div className={`w-2 h-2 rounded-full ${scanning && !error ? 'bg-green-500 animate-pulse' : 'bg-gray-300'}`}></div>
          <span className="text-gray-600">
            {scanning && !error ? 'Scanner active' : error ? 'Scanner error' : 'Processing'}
          </span>
        </div>
      </div>

      {/* Scanner Container */}
      <div className="relative bg-white border border-gray-200 rounded-lg overflow-hidden">
        {scanning && !error && (
          <div className="relative">
            <Scanner
              onScan={handleScan}
              onError={handleError}
              constraints={{
                facingMode: 'environment',
                aspectRatio: 1
              }}
              containerStyle={{
                width: '100%',
                height: '300px'
              }}
              videoStyle={{
                width: '100%',
                height: '100%',
                objectFit: 'cover'
              }}
            />
            
            {/* Scanning Overlay */}
            <div className="absolute inset-0 pointer-events-none">
              {/* Corner brackets */}
              <div className="absolute inset-4">
                <div className="relative w-full h-full border-2 border-white/30 rounded-lg">
                  {/* Top-left corner */}
                  <div className="absolute -top-1 -left-1 w-6 h-6 border-t-3 border-l-3 border-blue-500 rounded-tl-lg"></div>
                  {/* Top-right corner */}
                  <div className="absolute -top-1 -right-1 w-6 h-6 border-t-3 border-r-3 border-blue-500 rounded-tr-lg"></div>
                  {/* Bottom-left corner */}
                  <div className="absolute -bottom-1 -left-1 w-6 h-6 border-b-3 border-l-3 border-blue-500 rounded-bl-lg"></div>
                  {/* Bottom-right corner */}
                  <div className="absolute -bottom-1 -right-1 w-6 h-6 border-b-3 border-r-3 border-blue-500 rounded-br-lg"></div>
                </div>
              </div>
              
              {/* Scanning line animation */}
              <div className="absolute inset-4 overflow-hidden rounded-lg">
                <div className="absolute top-1/2 left-0 right-0 h-0.5 bg-gradient-to-r from-transparent via-blue-500 to-transparent animate-pulse transform -translate-y-1/2"></div>
              </div>
            </div>

            {/* Scanning status overlay */}
            <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/50 to-transparent p-4">
              <div className="flex items-center justify-center space-x-2 text-white">
                <div className="w-2 h-2 bg-blue-400 rounded-full animate-pulse"></div>
                <span className="text-sm font-medium">Scanning for QR codes...</span>
              </div>
            </div>
          </div>
        )}

        {/* Error State */}
        {error && (
          <div className="flex flex-col items-center justify-center h-72 p-6 text-center bg-gray-50">
            <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mb-4">
              <MdError className="w-8 h-8 text-red-600" />
            </div>
            <h4 className="font-semibold text-lg mb-2 text-gray-900">Scanner Error</h4>
            <p className="text-sm text-gray-600 mb-4 max-w-xs">{error}</p>
            <button
              onClick={resumeScanning}
              className="inline-flex items-center px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-medium transition-colors duration-200"
            >
              <MdRefresh className="mr-2" />
              Try Again
            </button>
          </div>
        )}

        {/* Processing State */}
        {processing && (
          <div className="flex flex-col items-center justify-center h-72 p-6 text-center bg-blue-50">
            <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mb-4">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
            </div>
            <h4 className="font-semibold text-lg mb-2 text-gray-900">Processing QR Code</h4>
            <p className="text-sm text-gray-600">Verifying registration details...</p>
          </div>
        )}

        {/* Success State (when not scanning and not processing) */}
        {!scanning && !error && !processing && (
          <div className="flex flex-col items-center justify-center h-72 p-6 text-center bg-green-50">
            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mb-4">
              <MdCheckCircle className="w-8 h-8 text-green-600" />
            </div>
            <h4 className="font-semibold text-lg mb-2 text-gray-900">QR Code Scanned</h4>
            <p className="text-sm text-gray-600 mb-4">Registration verified successfully</p>
            <button
              onClick={resumeScanning}
              className="inline-flex items-center px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-medium transition-colors duration-200"
            >
              <MdQrCodeScanner className="mr-2" />
              Scan Another
            </button>
          </div>
        )}
      </div>

      {/* Instructions */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mt-4">
        <div className="flex items-start space-x-3">
          <MdInfo className="text-blue-600 mt-0.5 flex-shrink-0" />
          <div>
            <h4 className="text-sm font-medium text-blue-900 mb-1">Scanning Instructions</h4>
            <ul className="text-sm text-blue-800 space-y-1">
              <li>• Hold your device steady and ensure good lighting</li>
              <li>• Position the QR code within the scanning frame</li>
              <li>• Wait for automatic detection and verification</li>
            </ul>
          </div>
        </div>
      </div>

      {/* Camera Permission Notice */}
      {error && error.includes('Camera') && (
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mt-4">
          <div className="flex items-start space-x-3">
            <MdCameraAlt className="text-yellow-600 mt-0.5 flex-shrink-0" />
            <div>
              <h4 className="text-sm font-medium text-yellow-900 mb-1">Camera Access Required</h4>
              <p className="text-sm text-yellow-800">
                Please allow camera access in your browser settings to use the QR scanner.
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default QRScanner;